/**
 * Launch aiming (pure; tested in test/aim.test.ts): what a click at (x, y) would do with the
 * selected silos / subs — zone under the cursor, friendly-fire warning, blast footprint and
 * flight time — plus the friendly-fire confirmation rule.
 */
import { missileFlightSeconds, type EntityView, type PlayerView, type WorldMap } from '@sa/sim';
import { clock } from '../state/alerts';
import { isFriendlyTo } from '@sa/protocol';
import { blastRingMu, nearestLandHex } from '../state/geo';
import type { AimState, AimZone } from '../state/store';
import { inSlbmRange, readyLaunchers } from './commands';
import { posNow } from '../net/interp';

/** A friendly-fire warning click must be confirmed within this window… */
export const FF_CONFIRM_MS = 1500;
/** …and near the same spot (CSS px). */
export const FF_CONFIRM_PX = 28;

/** Selected own launchers (silos, subs) that are not under construction. */
export function launchersOf(view: PlayerView, sel: readonly EntityView[]): EntityView[] {
  return sel.filter((e) => e.owner === view.me && (e.kind === 'silo' || e.kind === 'sub') && e.state !== 'building');
}

export function aimZone(view: PlayerView, world: WorldMap, x: number, y: number): { zone: AimZone; hex: number } {
  const hex = nearestLandHex(world, x, y);
  if (hex < 0) return { zone: 'water', hex };
  const o = view.owner[hex];
  if (o === view.me) return { zone: 'own', hex };
  if (isFriendlyTo(view, o)) return { zone: 'ally', hex };
  return { zone: o === 0 ? 'neutral' : 'enemy', hex };
}

/** Missiles loaded in a launcher (silo ICBMs, sub SLBMs). */
export const loadedOf = (e: EntityView): number => e.detail?.loaded ?? 0;

/** "ICBM", "SLBM" or "MISSILE" (a mixed selection), for launchers about to fire. */
function salvoWord(ls: readonly EntityView[]): string {
  const subs = ls.filter((e) => e.kind === 'sub').length;
  return subs === 0 ? 'ICBM' : subs === ls.length ? 'SLBM' : 'MISSILE';
}

/** "ICBM ×2" (one per ready launcher) or, rippling, "RIPPLE ×8" (every loaded missile). */
export function salvoLabel(ls: readonly EntityView[], n: number, ripple: boolean): string {
  if (ripple) return `RIPPLE ×${n}`;
  return `${salvoWord(ls)}${n > 1 ? ` ×${n}` : ''}`;
}

/**
 * Aim state for a launch at (x, y), or null if no launcher is selected. `ripple`: the click empties
 * every selected magazine that reaches (x, y), one missile per launcher as each is ready (Q twice).
 */
export function computeAim(view: PlayerView, world: WorldMap, sel: readonly EntityView[], x: number, y: number, confirmArmed = false, ripple = false): AimState | null {
  const launchers = launchersOf(view, sel);
  if (!launchers.length) return null;
  const { zone, hex } = aimZone(view, world, x, y);
  const ready = readyLaunchers(view, launchers);
  const reach = (e: EntityView) => e.kind !== 'sub' || inSlbmRange(e, view.tick, x, y);
  const inRange = ready.filter(reach);
  // (the ripple: every loaded launcher that reaches, ready now or after its reload)
  const loadedIn = launchers.filter((e) => loadedOf(e) > 0 && reach(e));
  const loaded = loadedIn.reduce((n, e) => n + loadedOf(e), 0);
  const primary = inRange[0] ?? ready[0] ?? (ripple ? loadedIn[0] : undefined) ?? launchers[0];
  const kind = primary.kind === 'sub' ? 'slbm' : 'icbm';
  const blocked = zone === 'ally';
  let reason: string | null = null;
  if (!view.released) reason = 'WEAPONS LOCKED';
  else if (blocked) reason = 'ALLY · BLOCKED';
  else if (ripple ? !launchers.some((e) => loadedOf(e) > 0) : !ready.length) reason = 'NO MISSILE READY';
  else if (ripple ? !loadedIn.length : !inRange.length) reason = 'OUT OF RANGE';
  const rings = [blastRingMu(world, 0), blastRingMu(world, 1), blastRingMu(world, 2)] as const;
  const from = posNow(primary, view.tick);
  return {
    x,
    y,
    launcherId: primary.id,
    kind,
    owner: hex >= 0 ? view.owner[hex] : 0,
    ownOrAllied: zone === 'own' || zone === 'ally',
    zone,
    fog: hex >= 0 && (zone === 'enemy' || zone === 'neutral') && !view.live[hex],
    blocked,
    confirmArmed: zone === 'own' && confirmArmed,
    ready: blocked ? 0 : inRange.length,
    loaded: blocked ? 0 : loaded,
    ripple,
    salvo: ripple ? salvoLabel(loadedIn, loaded, true) : salvoLabel(inRange, inRange.length, false),
    reason,
    blastRadiusMu: rings[2],
    blastRingsMu: rings,
    flightSeconds: missileFlightSeconds(kind, Math.hypot(from.x - x, from.y - y)),
  };
}

/** What a click does in launch aim. */
export type AimClick = 'fire' | 'warn' | 'refuse';

/**
 * Own land needs a deliberate second click (within `FF_CONFIRM_MS`, near the same spot) or
 * Shift; an ally's land and "nothing would fire" refuse; anything else fires.
 */
export function aimClick(aim: AimState, shift: boolean, armed: boolean): AimClick {
  if (aim.reason !== null || (aim.ready === 0 && !aim.ripple)) return 'refuse';
  if (aim.zone === 'own' && !shift && !armed) return 'warn';
  return 'fire';
}

/** Seconds to the first impact of a launch from `ls` at (x, y). */
export function firstImpact(ls: readonly EntityView[], tick: number, x: number, y: number): number {
  return Math.min(
    ...ls.map((e) => {
      const p = posNow(e, tick);
      return missileFlightSeconds(e.kind === 'sub' ? 'slbm' : 'icbm', Math.hypot(p.x - x, p.y - y));
    }),
  );
}

/** The launch confirmation: `2 ICBM AWAY · KAZAN · VULCAN · IMPACT 0:43` (whose land, when a rival's), `RIPPLE ×8 · KAZAN · IMPACT 0:43`. */
export function launchConfirm(salvo: string, place: string, impactSeconds: number, ripple: boolean, owner?: string): string {
  const what = ripple ? salvo : `${salvo.replace(/^(\w+) ×(\d+)$/, '$2 $1')} AWAY`;
  return `${what} · ${place}${owner ? ` · ${owner}` : ''} · IMPACT ${clock(impactSeconds)}`;
}
